import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Theme } from '../../../types';
import { useParams } from 'react-router-dom';
import { hasSound, isKlydoEditable } from '../../../utils';
import { useTheme } from '@mui/material';
import { Dayjs } from 'dayjs';
import useGetKlydoQuery from './useGetKlydoQuery';
import useCollectionQuery from '../../Collections/hooks/useCollectionQuery';
import usePalettesLocalStorage from './usePalettesLocalStorage';
import useHasAnyKlydosQuery from './useHasAnyKlydosQuery';
import { CollectionType } from '../../../api/collections';

const useKlydoScreen = () => {
  const { klydoId, collectionId } = useParams();
  const { data: klydo, isLoading } = useGetKlydoQuery(klydoId!);
  const { data: collection } = useCollectionQuery(collectionId!);

  const { spacing, palette } = useTheme();
  const { loadPalettes, savePalettes, clearPalettes } =
    usePalettesLocalStorage(klydoId);
  const [klydoUrl, setKlydoUrl] = useState<string | undefined>(klydo?.loopUrl);

  const [klydoSecureUrl, setKlydoSecureUrl] = useState<string | undefined>(
    klydo?.loopUrl,
  );

  const [suggestedColors, setSuggestedColors] = useState<Theme[] | undefined>(
    () => {
      const storedPalettes = loadPalettes();
      if (storedPalettes && storedPalettes.length > 0) {
        return storedPalettes;
      }
      return klydo?.theme ? [{ ...klydo?.theme }] : undefined;
    },
  );

  const [minutesValue, setMinutesValue] = useState(10);
  const [hoursValue, setHoursValue] = useState(22);
  const [isDialsHidden, setIsDialsHidden] = useState(false);
  const [isTimpePickerOpen, setIsTimePickerOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<Theme | undefined>(
    klydo?.theme,
  );
  const [isEditingState, setIsEditingState] = useState(false);
  const [isAnimationPaused, setIsAnimationPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isFirstKlydoDialogOpen, setIsFirstKlydoDialogOpen] = useState(false);
  const hasShownFirstKlydoDialog = useRef(false);

  const handleHideDialsClick = useCallback(() => {
    setIsDialsHidden((prev) => !prev);
  }, []);

  const handleAnimationPauseToggle = useCallback(() => {
    setIsAnimationPaused((prev) => !prev);
  }, []);

  const handleMuteToggle = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!klydo || !klydoId) return;

    setKlydoSecureUrl(klydo.loopUrl);
    setSelectedTheme(klydo.theme);

    const storedPalettes = loadPalettes();
    if (storedPalettes && storedPalettes.length > 0) {
      setSuggestedColors(storedPalettes);
    } else if (klydo.theme) {
      setSuggestedColors([{ ...klydo.theme }]);
    }
  }, [klydo?.id, klydoId]);

  useEffect(() => {
    if (!klydoId || !suggestedColors || suggestedColors.length === 0) return;

    const timeoutId = setTimeout(() => {
      savePalettes(suggestedColors);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [klydoId, suggestedColors]);

  const previewKlydo = useMemo(() => {
    const mode = klydo?.capabilitiesV2?.animationSound?.mode;
    return {
      loopUrl: klydoUrl || klydoSecureUrl,
      theme: selectedTheme,
      ...(mode && {
        capabilitiesV2: {
          animationSound: { mode },
        },
      }),
    };
  }, [
    klydoUrl,
    klydoSecureUrl,
    selectedTheme,
    klydo?.capabilitiesV2?.animationSound?.mode,
  ]);

  const isEditableView = useMemo(() => {
    if (isEditingState) return true;
    // Two separate locks, kept separate on purpose. The seal is structural; a live
    // verdict is a reviewer holding the klydo. Keyed on `state`, not on the review
    // record existing — a migrated draft can carry a record with no state, and
    // treating that as "under review" made it uneditable with no way forward.
    const isUnderReview = !!klydo?.public?.reviewStatus?.state;
    return isKlydoEditable(klydo) && !isUnderReview;
  }, [klydo, isEditingState]);

  useEffect(() => {
    if (
      isEditableView &&
      !hasSound(klydo?.capabilitiesV2?.animationSound?.mode)
    ) {
      setIsMuted(true);
    }
  }, [klydo?.capabilitiesV2?.animationSound?.mode, isEditableView]);

  const { data: hasAnyKlydos, isLoading: isHasAnyKlydosLoading } =
    useHasAnyKlydosQuery(isEditableView);

  // Show a one-time welcome popup when a first-time user (no klydos yet)
  // opens the create form.
  useEffect(() => {
    if (
      isEditableView &&
      !isHasAnyKlydosLoading &&
      hasAnyKlydos === false &&
      !hasShownFirstKlydoDialog.current
    ) {
      hasShownFirstKlydoDialog.current = true;
      setIsFirstKlydoDialogOpen(true);
    }
  }, [isEditableView, isHasAnyKlydosLoading, hasAnyKlydos]);

  const handleCloseFirstKlydoDialog = useCallback(() => {
    setIsFirstKlydoDialogOpen(false);
  }, []);

  const menuButtonItemsList = useMemo(() => {
    const baseItems = [
      {
        title: isDialsHidden ? 'Show Dials' : 'Hide Dials',
        isDisabled: false,
        onClick: () => handleHideDialsClick(),
      },
      {
        title: 'Change Time View',
        isDisabled: false,
        onClick: () => setIsTimePickerOpen(true),
      },
    ];

    if (hasSound(klydo?.capabilitiesV2?.animationSound?.mode)) {
      baseItems.push({
        title: isMuted ? 'Unmute' : 'Mute',
        isDisabled: false,
        onClick: () => handleMuteToggle(),
      });
    }

    if (isEditableView) {
      baseItems.push({
        title: isAnimationPaused ? 'Resume Animation' : 'Pause Animation',
        isDisabled: !klydoSecureUrl,
        onClick: () => handleAnimationPauseToggle(),
      });
    }

    return baseItems;
  }, [
    handleHideDialsClick,
    isEditableView,
    isAnimationPaused,
    klydoSecureUrl,
    handleAnimationPauseToggle,
    isDialsHidden,
    klydo?.public?.reviewStatus?.state,
    klydo?.capabilitiesV2?.animationSound?.mode,
    isMuted,
    handleMuteToggle,
  ]);

  const handleClosedTimePicker = useCallback((time: Dayjs | null) => {
    setMinutesValue(time?.minute() || 0);
    setHoursValue(time?.hour() || 0);
    setIsTimePickerOpen(false);
  }, []);

  const handleKlydoThemeChange = useCallback((theme: Theme | undefined) => {
    setSelectedTheme(theme);
  }, []);

  const handleLocalUrlChange = useCallback(
    (url: string) => {
      setKlydoUrl(url);
    },
    [klydoUrl],
  );

  const handleSecuredUrlChange = useCallback(
    (url: string) => {
      setKlydoSecureUrl(url);
      if (url && klydoUrl) {
        setTimeout(() => {
          setKlydoUrl('');
        }, 200);
      }
    },
    [klydoSecureUrl, klydoUrl],
  );

  const handleBackToEditClick = () => {
    // Re-editing a klydo that had already entered review: drop the stored
    // palettes and reset to its saved theme.
    if (klydoId && klydo && klydo.public?.reviewStatus) {
      clearPalettes();
      if (klydo.theme) {
        setSuggestedColors([{ ...klydo.theme }]);
      }
    }
    setIsEditingState(true);
  };

  const handleSubmitKlydo = () => {
    setIsEditingState(false);
  };

  /**
   * Hold the editor mounted while the post-submit confirmation is on screen.
   *
   * Without this the editor unmounts under the artist: `onSettled` invalidates
   * `['klydo', id]`, the refetch comes back with a PENDING review state, and
   * `isEditableView`'s data branch flips to false — taking `PublishSuccessModal`
   * (rendered inside the editor) with it, so the confirmation flashes and
   * disappears and `handleSubmitKlydo` never runs. Pinning `isEditingState`
   * keeps the editor up until the modal is dismissed, which then calls
   * `handleSubmitKlydo` and hands the klydo to the read-only view.
   */
  const handleKeepEditorMounted = useCallback(() => {
    setIsEditingState(true);
  }, []);

  return {
    suggestedColors,
    setSuggestedColors,
    klydoSecureUrl,
    minutesValue,
    hoursValue,
    isDialsHidden,
    spacing,
    menuButtonItemsList,
    isTimpePickerOpen,
    handleClosedTimePicker,
    klydo,
    isEditableView,
    isEditingState,
    palette,
    klydoUrl,
    setSelectedTheme,
    handleKlydoThemeChange,
    selectedTheme,
    handleLocalUrlChange,
    handleSecuredUrlChange,
    klydoId,
    handleBackToEditClick,
    isLoading,
    handleSubmitKlydo,
    handleKeepEditorMounted,
    isAnimationPaused,
    collectionLogo: collection?.logoUrl,
    // Draft-only field on the api - gone entirely once a collection is
    // approved (its logoUrl is already baked to the right size by then).
    collectionPendulumLogoSize:
      collection?.type === CollectionType.Draft
        ? collection.pendulumLogoSize
        : undefined,
    // NOT draft-only, unlike pendulumLogoSize above - approval never erases
    // it, and it's still needed to know whether the baked logoUrl renders
    // tinted ('sized') or in its own true colors ('full').
    collectionPendulumLogoType: collection?.pendulumLogoType,
    isApprovedCollection: collection?.type === CollectionType.Approved,
    previewKlydo,
    isMuted,
    handleMuteToggle,
    isFirstKlydoDialogOpen,
    handleCloseFirstKlydoDialog,
  };
};

export default useKlydoScreen;
